import React, { useEffect, useState } from 'react';
import { useTheme } from '@emotion/react';
import '../../assets/css/main.css';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Select,
  styled,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { DateRangePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import CircularProgressBar from 'ui-component/CircularProgressBar';
import HistoryList from './HistoryList';
import TransactionHistory from './TransactionHistory';
import getAxiosInstance from 'api';
import moment from 'moment';
import toast from 'react-hot-toast';
import { getDateRangeFromQuickOption } from 'utils/common';
import Errors from 'views/errors';
import RevenueDashboard from 'views/dashboard/Revenue';
import CustomerCallsTab from 'views/dashboard/Analytics/CustomerCallsTab';
const StyledCard = styled(Card)(({ theme }) => ({
  padding: '24px',
  boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.06)',
  borderRadius: '12px',
  border: '1px solid rgba(0, 0, 0, 0.1)',
}));

const DateRangeButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#fff',
  color: theme.palette.dark.main,

  border: '1px solid rgba(0, 0, 0, 0.1)',
  borderRadius: '8px',
  padding: '8px 16px',
  textTransform: 'none',
  fontWeight: 400,
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
}));

const quickRanges = [
  { label: 'Current Day', value: 'day' },
  { label: 'Current Week', value: 'week' },
  { label: 'Current Month', value: 'month' },
  { label: 'Last 3 Months', value: '3months' },
  { label: 'Last 6 Months', value: '6months' },
  { label: 'Last 12 Months', value: '12months' },
  { label: 'Quarter to date', value: 'quarter' },
  { label: 'All Time', value: 'all' },
];

const Calls = () => {
  const theme = useTheme();
  const location = useLocation();
  const [tab, setTab] = useState(location.state?.activeTab ?? 0);
  const [callData, setCallData] = useState([]);
  const [productsDate, setProductsDate] = useState([
    moment().startOf('month').toDate(),
    moment().toDate(),
  ]);

  const [callsDate, setCallsDate] = useState([
    moment().startOf('month').toDate(),
    moment().toDate(),
  ]);

  const [customerDate, setCustomerDate] = useState([
    moment('2020-01-01').toDate(),
    moment().toDate(),
  ]);
  const [productData, setProductData] = useState([]);
  const [customerData, setCustomerData] = useState([]);
  const [loadingCalls, setLoadingCalls] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [callsQuickRange, setCallsQuickRange] = useState('');
  const [productsQuickRange, setProductsQuickRange] = useState('');
  const [customersQuickRange, setCustomersQuickRange] = useState('all');

  useEffect(() => {
    if (!customerDate || customerDate.length !== 2) return;

    const [start, end] = customerDate;
    const isAllTime =
      moment(start).isSame(moment('2020-01-01'), 'day') &&
      moment(end).isSame(moment(), 'day');

    if (isAllTime && customersQuickRange !== 'all') {
      setCustomersQuickRange('all');
    } else if (!isAllTime && customersQuickRange === 'all') {
      setCustomersQuickRange('');
    }
  }, [customerDate, customersQuickRange]);

  useEffect(() => {
    fetchCallsCount();
  }, [callsDate]);
  useEffect(() => {
    fetchProductCount();
  }, [productsDate]);
  useEffect(() => {
    fetchCustomerCount();
  }, [customerDate]);
  const fetchCallsCount = () => {
    setLoadingCalls(true);
    getAxiosInstance(
      `adminOrchestrator/dashboard/getActiveProductCounts`,
      'post',
      {
        startDate: moment(callsDate[0]).format('MM-DD-YYYY'),
        endDate: moment(callsDate[1]).format('MM-DD-YYYY'),
      },
      { 'journey-type': 'Dashboard' }
    )
      .then((res) => {
        if (res?.data?.codes === 200) {
          setLoadingCalls(false);
          setCallData([
            {
              title: 'Total',
              value: res.data.data.totalCount,
              progress:
                (res.data.data.totalCount / res.data.data.totalCount) * 100 ||
                0,
              isDrillDown: false,
            },
            {
              title: 'API',
              value: res.data.data.apiCount,
              progress:
                (res.data.data.apiCount / res.data.data.totalCount) * 100 || 0,
              isDrillDown: true,
              route: 'api',
            },
            {
              title: 'Dashboard',
              value: res.data.data.onlineCount,
              progress:
                (res.data.data.onlineCount / res.data.data.totalCount) * 100 ||
                0,
              isDrillDown: true,
              route: 'dashboard',
            },
            {
              title: 'Connect',
              value: res.data.data.connectDashboardCallCount,
              progress:
                (res.data.data.connectDashboardCallCount /
                  res.data.data.totalCount) *
                  100 || 0,
              isDrillDown: true,
              route: 'connect',
            },
          ]);
        }
      })
      .catch((err) => {
        setLoadingCalls(false);
        toast.error(err?.response?.data?.message);
      });
  };
  const fetchProductCount = () => {
    setLoadingProducts(true);
    getAxiosInstance(
      `adminOrchestrator/dashboard/getConsentCountByDateRange`,
      'post',
      {
        startDate: moment(productsDate[0]).format('MM-DD-YYYY'),
        endDate: moment(productsDate[1]).format('MM-DD-YYYY'),
      },
      { 'journey-type': 'Dashboard' }
    )
      .then((res) => {
        if (res?.data?.codes === 200) {
          setLoadingProducts(false);
          setProductData([
            {
              title: 'Credit Assessment',
              value: res.data.data.creditRiskCount,
              progress: 100,
              route: 'credit-assessment',
              isDrillDown: true,
            },
            {
              title: 'BVN',
              value: res.data.data.bvnCount,
              progress: 100,
              route: 'bvn',
              isDrillDown: true,
            },
            {
              title: 'NIN',
              value: res.data.data.ninCount || 0,
              progress: 100,
              route: 'nin',
              isDrillDown: true,
            },
            {
              title: 'CAC',
              value: res.data.data.cacCount || 0,
              progress: 100,
              route: 'cac',
              isDrillDown: true,
            },
          ]);
        }
      })
      .catch((err) => {
        setLoadingProducts(false);
        toast.error(err?.response?.data?.message);
      });
  };
  const fetchCustomerCount = () => {
    setLoadingCustomers(true);
    getAxiosInstance(
      `adminOrchestrator/dashboard/getBpAndUsersCount`,
      'post',
      {
        startDate: moment(customerDate[0]).format('MM-DD-YYYY'),
        endDate: moment(customerDate[1]).format('MM-DD-YYYY'),
      },
      { 'journey-type': 'Dashboard' }
    )
      .then((res) => {
        if (res?.data?.codes === 200) {
          setLoadingCustomers(false);
          setCustomerData([
            {
              title: 'Total Customer',
              value:
                Number(res.data.data.userCount || 0) +
                Number(res.data.data.apiBPCount || 0) +
                Number(res.data.data.onlineBPCount || 0),
              progress: 100,
              subtitle: 'Total',
            },
            {
              title: 'API Customers',
              value: res.data.data.apiBPCount,
              progress:
                (res.data.data.apiBPCount /
                  (Number(res.data.data.userCount || 0) +
                    Number(res.data.data.apiBPCount || 0) +
                    Number(res.data.data.onlineBPCount || 0))) *
                100 || 0,
              subtitle: 'API',
              isDrillDown: true,
              route: 'api',
            },
            {
              title: 'Dashboard Customers',
              value: res.data.data.onlineBPCount,
              progress:
                (res.data.data.onlineBPCount /
                  (Number(res.data.data.userCount || 0) +
                    Number(res.data.data.apiBPCount || 0) +
                    Number(res.data.data.onlineBPCount || 0))) *
                100 || 0,
              subtitle: 'Dashboard',
              isDrillDown: true,
              route: 'dashboard',
            },
            {
              title: 'Connect Customers',
              value: res.data.data.userCount,
              progress:
                (res.data.data.userCount /
                  (Number(res.data.data.userCount || 0) +
                    Number(res.data.data.apiBPCount || 0) +
                    Number(res.data.data.onlineBPCount || 0))) *
                100 || 0,
              subtitle: 'Total',
              isDrillDown: true,
              route: 'connect',
            },
          ]);
        }
      })
      .catch((err) => {
        setLoadingCustomers(false);
        toast.error(err?.response?.data?.message);
      });
  };
  const navigate = useNavigate();

  const handleViewDrilldown = (route) => {
    navigate(`/calls/${route}`);
  };

  const handleUserViewDrilldown = (route) => {
    // Pass filter type as state
    let filterType = '';
    if (route === 'api') filterType = 'API';
    if (route === 'dashboard') filterType = 'Online';
    if (route === 'connect') filterType = 'User';
    navigate(`/calls/${route}/users`, { state: { filterType } });
  };

  return (
    <Box sx={{ p: 3 }}>

      {/* Tabs: Calls | Counts | Revenue */}
      <Tabs
        value={tab}
        onChange={(e, val) => setTab(val)}
        sx={{
          '& .MuiTabs-indicator': {
            backgroundColor: '#FC5719',
          },
        }}
      >
        <Tab
          label="Calls"
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': { color: '#FC5719' },
            '&.Mui-selected': { color: '#FC5719' },
          }}
        />
        <Tab
          label="Counts"
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': { color: '#FC5719' },
            '&.Mui-selected': { color: '#FC5719' },
          }}
        />
        <Tab
          label="Revenue"
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': { color: '#FC5719' },
            '&.Mui-selected': { color: '#FC5719' },
          }}
        />
      </Tabs>

      {/* Tab 0: Calls / Products / Customers dashboard */}
      {tab === 0 && (
        <>
          <Box mt={2}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 500, color: '#212120' }}>
                Calls
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <Select
                    value={callsQuickRange}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCallsQuickRange(value);
                      if (value) {
                        const range = getDateRangeFromQuickOption(value);
                        if (range) {
                          setCallsDate([range.start, range.end]);
                        }
                      }
                    }}
                    displayEmpty
                    sx={{ backgroundColor: '#fff' }}
                  >
                    <MenuItem value="">Quick Range</MenuItem>
                    {quickRanges.map((range) => (
                      <MenuItem key={range.value} value={range.value}>
                        {range.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <DateRangePicker
                  appearance="default"
                  placeholder="Select Date Range"
                  value={callsDate}
                  onChange={(value) => {
                    setCallsDate([value[0], value[1]]); // Γ£à FIXED
                    setCallsQuickRange('');
                  }}
                  showOneCalendar
                />
              </Box>
            </Box>

            <StyledCard>
              {loadingCalls ? (
                <Box display="flex" justifyContent="center" py={5}>
                  <CircularProgress color="secondary" />
                </Box>
              ) : (
                  <Grid container spacing={4} justifyContent="center">
                    {callData?.map((call, index) => (
                      <Grid item xs={12} sm={3} key={index} sx={{ textAlign: 'center' }}>
                        <Box sx={{ maxWidth: 200, margin: '0 auto' }}>

                          <CircularProgressBar
                            width={100}
                            size={150}
                            title={call.title}
                            value={call.value}
                            progress={call.progress}
                          />
                          {call?.isDrillDown && (
                            <Button
                              variant="text"
                              onClick={() => handleViewDrilldown(call.route)}
                              startIcon={<KeyboardArrowUpIcon />}
                              sx={{
                                color: '#FC5719',
                                textTransform: 'none',
                                mt: 1,
                              }}
                            >
                              View Drilldown
                            </Button>
                          )}

                        </Box>
                      </Grid>
                    ))}
                  </Grid>
              )}
            </StyledCard>
          </Box>

          {/* PRODUCTS */}
          <Box mt={2}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 500, color: '#212120' }}>
                Products
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <Select
                    value={productsQuickRange}
                    onChange={(e) => {
                      const value = e.target.value;
                      setProductsQuickRange(value);
                      if (value) {
                        const range = getDateRangeFromQuickOption(value);
                        if (range) {
                          setProductsDate([range.start, range.end]);
                        }
                      }
                    }}
                    displayEmpty
                    sx={{ backgroundColor: '#fff' }}
                  >
                    <MenuItem value="">Quick Range</MenuItem>
                    {quickRanges.map((range) => (
                      <MenuItem key={range.value} value={range.value}>
                        {range.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <DateRangePicker
                  appearance="default"
                  placeholder="Select Date Range"
                  value={productsDate}
                  onChange={(value) => {
                    setProductsDate([value[0], value[1]]);
                    setProductsQuickRange('');
                  }}
                  showOneCalendar
                />
              </Box>
            </Box>

            <StyledCard>
              {loadingProducts ? (
                <Box display="flex" justifyContent="center" py={5}>
                  <CircularProgress color="secondary" />
                </Box>
              ) : (
                <Grid container spacing={4} justifyContent="center">
                  {productData.map((item, index) => (
                    <Grid item xs={12} sm={3} key={index} sx={{ textAlign: 'center' }}>
                      <Box sx={{ maxWidth: 200, margin: '0 auto' }}>
                        <CircularProgressBar
                          width={100}
                          size={150}
                          title={'Total calls'}
                          value={item?.value}
                          progress={item?.progress}
                          header={item?.title}
                        />
                        {item?.isDrillDown && (
                          <Button
                            variant="text"
                            onClick={() => handleViewDrilldown(item.route)}
                            startIcon={<KeyboardArrowUpIcon />}
                            sx={{
                              color: '#FC5719',
                              textTransform: 'none',
                            }}
                          >
                            View Drilldown
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </StyledCard>
          </Box>

          {/* CUSTOMERS */}
          <Box mt={2}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 500, color: '#212120' }}>
                Customers
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <Select
                    value={customersQuickRange}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCustomersQuickRange(value);
                      if (value) {
                        const range = getDateRangeFromQuickOption(value);
                        if (range) {
                          setCustomerDate([range.start, range.end]);
                        }
                      }
                    }}
                    displayEmpty
                    sx={{ backgroundColor: '#fff' }}
                  >
                    <MenuItem value="">Quick Range</MenuItem>
                    {quickRanges.map((range) => (
                      <MenuItem key={range.value} value={range.value}>
                        {range.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <DateRangePicker
                  appearance="default"
                  placeholder="Select Date Range"
                  value={customerDate}
                  onChange={(value) => {
                    setCustomerDate([value[0], value[1]]);
                    setCustomersQuickRange('');
                  }}
                  showOneCalendar
                />
              </Box>
            </Box>

            <StyledCard>
              {loadingCustomers ? (
                <Box display="flex" justifyContent="center" py={5}>
                  <CircularProgress color="secondary" />
                </Box>
              ) : (
                <Grid container spacing={4} justifyContent="center">
                  {customerData.map((item, index) => (
                    <Grid item xs={12} sm={3} key={index} sx={{ textAlign: 'center' }}>
                      <Box sx={{ maxWidth: 200, margin: '0 auto' }}>
                        <CircularProgressBar
                          width={100}
                          size={150}
                          title={item?.subtitle}
                          value={item?.value}
                          progress={item?.progress}
                          header={item?.title}
                        />
                        {item?.isDrillDown && (
                          <Button
                            variant="text"
                            onClick={() => handleUserViewDrilldown(item.route)}
                            startIcon={<KeyboardArrowUpIcon />}
                            sx={{
                              color: '#FC5719',
                              textTransform: 'none',
                            }}
                          >
                            View Drilldown
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </StyledCard>
          </Box>

          <Errors />
          {/* <HistoryList /> */}
          <TransactionHistory />
        </>
      )}

      {/* Tab 1: Count dashboard */}
      {tab === 1 && <CustomerCallsTab />}

      {/* Tab 2: Revenue dashboard */}
      {tab === 2 && <RevenueDashboard />}

    </Box>
  );
};
export default Calls;
